import React from 'react';
import { GdModal, Button, CardBody, Toast } from 'gd-react';
import ModifierEditor from './ModifierEditor';
import GridDuck from 'gridduck';
import './ModifiersModal.scss';

class ModifiersModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasChanges: false,
            isSaving: false,
            showToast: false,
            toastMessage: '',
            toastType: 'success',
            modifier: {}
        };
        this.onChange = this.onChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.saveModifier = this.saveModifier.bind(this);
    }

    onChange(modifier) {
        this.setState({ 
            hasChanges: true,
            modifier: modifier
        });
    }

    async handleSave() {
        this.setState({ isSaving: true });
        
        try {
            await this.saveModifier();
            this.setState({ 
                hasChanges: false, 
                showToast: true, 
                toastMessage: 'Modifier saved successfully',
                toastType: 'success'
            });
            
            // Close modal after a short delay to show the toast
            setTimeout(() => {
                this.props.onClose();
            }, 1500);
            
        } catch (error) {
            this.setState({ 
                showToast: true, 
                toastMessage: 'Failed to save modifier: ' + error.message,
                toastType: 'error'
            });
        } finally {
            this.setState({ isSaving: false });
        }
    }

    async saveModifier() {
        const { editingModifier, editingDefaults, asset } = this.props;
        const { modifier } = this.state;
        
        // Determine the level we're saving to
        let level = 'asset'; // default
        let modifierKey = 'defaultModifiers';
        
        if (editingDefaults === 'org') {
            level = 'organisation';
            modifierKey = 'organisationDefaultModifiers';
        } else if (editingDefaults === 'site') {
            level = 'site';
            modifierKey = 'siteDefaultModifiers';
        }
        
        // Prepare the update data
        let updateData = {};
        
        // Get the current modifier array for this level
        let currentModifiers = asset[modifierKey] || {};
        
        // Update or add the modifier to the array
        const updatedModifiers = {
            ...currentModifiers,
            [editingModifier]: modifier
        };
        
        // Set the modifier array in the update data
        updateData[modifierKey] = updatedModifiers;
        
        // Update the modifierLevel to reflect the current level being used
        // modifierLevel should be an object mapping modifier IDs to their levels
        let currentModifierLevel = asset.modifierLevel || {};
        
        // Update the modifierLevel for this specific modifier
        const updatedModifierLevel = {
            ...currentModifierLevel,
            [editingModifier]: level
        };
        
        // Update the modifierLevel
        updateData.modifierLevel = updatedModifierLevel;
        
        // Save all updates to the asset
        await asset.set(updateData);
        
        // Notify parent to reload data
        if (this.props.onClose) {
            this.props.onClose(true); // true indicates should reload
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.editingModifier !== this.props.editingModifier || 
            prevProps.modifierToEdit !== this.props.modifierToEdit) {
            // Reset state when editing a different modifier or modifier data changes
            this.initializeModifierState();
        }
    }

    componentDidMount() {
        // Initialize modifier state when component mounts
        this.initializeModifierState();
    }

    initializeModifierState() {
        const { editingModifier, modifierToEdit } = this.props;
        
        if (editingModifier && modifierToEdit) {
            this.setState({ 
                hasChanges: false,
                modifier: modifierToEdit
            });
        }
    }

    handleCancel() {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
        this.setState({ hasChanges: false });
    }

    getModifierDisplayName(key) {
        const { value } = this.props;
        const modifier = value[key];
        
        // Use the modifier's name field if available, otherwise fall back to the key
        return modifier?.name || key;
    }

    render() {
        const { open, onClose, value, asset, editingModifier, editingDefaults = false, creatingNewModifier = false } = this.props;
        const { hasChanges, isSaving, showToast, toastMessage, toastType, modifier } = this.state;

        if (!editingModifier) {
            return null;
        }

        const modifierName = this.getModifierDisplayName(editingModifier) || 'Modifier';
        let titleWithModifier;
        
        if (creatingNewModifier) {
            let levelText = 'Device Level';
            if (editingDefaults === 'org') {
                levelText = 'Organization Level';
            } else if (editingDefaults === 'site') {
                levelText = 'Site Level';
            }
            titleWithModifier = `Create ${modifierName} (${levelText})`;
        } else {
            let levelText = 'Device Level';
            if (editingDefaults === 'org') {
                levelText = 'Organization Level';
            } else if (editingDefaults === 'site') {
                levelText = 'Site Level';
            }
            titleWithModifier = `Edit ${modifierName} (${levelText})`;
        }

        const footer = (
            <div className="modal-footer-actions">
                <Button
                    label="Cancel"
                    onClick={this.handleCancel}
                    disabled={isSaving}
                    additionalclasses="sm"
                />
                <Button
                    label={isSaving ? "Saving..." : (creatingNewModifier ? "Create Modifier" : "Save Changes")}
                    color="gd-green"
                    onClick={this.handleSave}
                    disabled={!hasChanges || isSaving}
                    additionalclasses="sm"
                />
            </div>
        );

        return (
            <>
                <GdModal
                    open={open}
                    onClose={onClose}
                    contentLabel={'Edit Modifier'}
                    title={titleWithModifier}
                    footer={footer}
                    wide={true}
                >
                    <CardBody additionalclasses={'no-padding rule full-height'}>
                        <ModifierEditor
                            modifierKey={editingModifier}
                            modifier={modifier}
                            onChange={this.onChange}
                            editingDefaults={editingDefaults}
                        />
                    </CardBody>
                </GdModal>
                
                {showToast && (
                    <Toast
                        open={showToast}
                        onClose={() => this.setState({ showToast: false })}
                        message={toastMessage}
                        severity={toastType}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    />
                )}
            </>
        );
    }
}

export default ModifiersModal;
